// src/composables/useResponsive.js
import { ref, computed, onMounted, onUnmounted, reactive } from 'vue'
import { useQuasar } from 'quasar'

export function useResponsive() {
  const $q = useQuasar()
  
  // Platform detection
  const isMobile = computed(() => $q.platform.is.mobile)
  const isDesktop = computed(() => !$q.platform.is.mobile)
  const isMobileOrTablet = computed(() => isMobile.value || isXs.value || isSm.value)
  const isTablet = computed(() => (isSm.value || (isLandscape.value && isXs.value)) && !$q.platform.is.mobile)
  
  // Screen size breakpoints
  const screenWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 0)
  const screenHeight = ref(typeof window !== 'undefined' ? window.innerHeight : 0)
  
  // Media query breakpoints matching Quasar's defaults
  const isXs = computed(() => screenWidth.value < 600)
  const isSm = computed(() => screenWidth.value >= 600 && screenWidth.value < 1024)
  const isMd = computed(() => screenWidth.value >= 1024 && screenWidth.value < 1440)
  const isLg = computed(() => screenWidth.value >= 1440 && screenWidth.value < 1920)
  const isXl = computed(() => screenWidth.value >= 1920)
  
  // Map Quasar breakpoints to Bootstrap equivalents for migration ease
  const isBootstrapSm = computed(() => screenWidth.value >= 576)
  const isBootstrapMd = computed(() => screenWidth.value >= 768)
  const isBootstrapLg = computed(() => screenWidth.value >= 992)
  const isBootstrapXl = computed(() => screenWidth.value >= 1200)
    // Update screen dimensions on resize
  const updateScreenDimensions = () => {
    screenWidth.value = window.innerWidth
    screenHeight.value = window.innerHeight
  }
  
  // Setup resize listener
  onMounted(() => {
    window.addEventListener('resize', updateScreenDimensions)
    updateScreenDimensions()
  })
  
  // Cleanup
  onUnmounted(() => {
    window.removeEventListener('resize', updateScreenDimensions)
  })
  
  // Device orientation
  const isPortrait = computed(() => $q.screen.height > $q.screen.width)
  const isLandscape = computed(() => !isPortrait.value)
  
  // Touch capability
  const hasTouch = computed(() => $q.platform.has.touch)
  
  // PWA and offline detection
  const isOffline = ref(typeof navigator !== 'undefined' ? !navigator.onLine : false)
  const isPwa = ref(false)
  
  // Check if running as installed PWA
  const checkIfPwa = () => {
    if (typeof window === 'undefined') return false
    
    // Check if app is displayed in standalone mode (PWA installed)
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches
    const isFromHomeScreen = 
      window.navigator.standalone || // iOS
      isStandalone // Android/Chrome
    
    isPwa.value = isFromHomeScreen
  }
  
  // Monitor online/offline status
  const setupOfflineDetection = () => {
    if (typeof window === 'undefined') return
    
    const updateOnlineStatus = () => {
      isOffline.value = !navigator.onLine
    }
    
    window.addEventListener('online', updateOnlineStatus)
    window.addEventListener('offline', updateOnlineStatus)
    
    // Cleanup
    onUnmounted(() => {
      window.removeEventListener('online', updateOnlineStatus)
      window.removeEventListener('offline', updateOnlineStatus)
    })
  }
    // Device type specific classes
  const deviceClasses = computed(() => {
    return {
      'is-mobile': isMobile.value,
      'is-tablet': isTablet.value,
      'is-desktop': isDesktop.value,
      'is-xs': isXs.value,
      'is-sm': isSm.value,
      'is-md': isMd.value,
      'is-lg': isLg.value,
      'is-xl': isXl.value,
      'is-portrait': isPortrait.value,
      'is-landscape': isLandscape.value,
      'has-touch': hasTouch.value,
      'is-offline': isOffline.value,
      'is-pwa': isPwa.value
    }
  })
  
  // Utility function to determine column sizes based on screen size
  // This helps with the transition from Bootstrap's grid system to Quasar's
  const getColSize = (xs = 12, sm = 6, md = 4, lg = 3, xl = 3) => {
    if (isXs.value) return xs
    if (isSm.value) return sm
    if (isMd.value) return md
    if (isLg.value) return lg
    return xl
  }
  
  // Initialize offline and PWA detection
  if (typeof window !== 'undefined') {
    onMounted(() => {
      checkIfPwa()
      setupOfflineDetection()
      
      // Listen for display mode changes (e.g., if user opens PWA from browser)
      window.matchMedia('(display-mode: standalone)').addEventListener('change', checkIfPwa)
    })
  }
  
  return {
    // Screen properties
    screenWidth,
    screenHeight,
    isXs,
    isSm,
    isMd,
    isLg,
    isXl,
    
    // Bootstrap equivalents (for migration ease)
    isBootstrapSm,
    isBootstrapMd,
    isBootstrapLg,
    isBootstrapXl,
    
    // Device types
    isMobile,
    isTablet,
    isDesktop,
    isMobileOrTablet,
    hasTouch,
    
    // Orientation
    isPortrait,
    isLandscape,
    
    // PWA and offline
    isOffline,
    isPwa,
    
    // Helper functions
    getColSize,
    
    // Classes
    deviceClasses
  }
}
